import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { FALLBACK_BRANCHES, IMAGES } from "@/data/site";

export default function Branches() {
  const [branches, setBranches] = useState(FALLBACK_BRANCHES);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api
      .get("/branches")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setBranches(res.data);
      })
      .catch(() => {});
  }, []);

  const b = branches[active] || branches[0];

  return (
    <main className="pt-28 md:pt-36">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">Our Branches</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Three locations. <span className="text-gold-gradient">One oxygen.</span>
          </h1>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
          {branches.map((br, i) => (
            <button
              key={br.slug}
              data-testid={`branch-select-${br.slug}`}
              onClick={() => setActive(i)}
              className={`px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                active === i
                  ? "bg-gold text-ink border-gold font-semibold"
                  : "border-white/15 text-white/60 hover:border-gold hover:text-gold"
              }`}
            >
              {br.name}
            </button>
          ))}
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 md:py-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={b.slug}
            data-testid={`branch-panel-${b.slug}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5 }}
            className="grid gap-10 lg:grid-cols-2 items-stretch"
          >
            <div className="relative overflow-hidden clip-frame border border-white/10 min-h-[320px]">
              <img src={b.image} alt={`Oxygen Gym ${b.name} interior`} className="absolute inset-0 h-full w-full object-cover opacity-80" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
              <p className="absolute bottom-6 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                {b.label}
              </p>
            </div>
            <div className="clip-frame border border-white/10 bg-panel p-8 md:p-10 flex flex-col">
              <h2 className="font-display uppercase text-3xl md:text-4xl">{b.name}</h2>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">{b.address}</p>
              <div className="mt-8 space-y-5 flex-1">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">Timings</p>
                  <p className="text-sm text-white/70">{b.timings}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">Phone / WhatsApp</p>
                  <p className="text-sm text-white/70">{b.phone}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">Amenities</p>
                  <div className="flex flex-wrap gap-2">
                    {b.amenities.map((a) => (
                      <span key={a} className="border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-white/65">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-3">Specialties</p>
                  <div className="flex flex-wrap gap-2">
                    {b.specialties.map((s) => (
                      <span key={s} className="bg-gold/10 border border-gold/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-gold">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  data-testid={`branch-enquiry-${b.slug}`}
                  to={`/contact?branch=${b.slug}`}
                  className="bg-gold text-ink px-6 py-3.5 font-bold uppercase text-xs tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
                >
                  Enquire For This Branch
                </Link>
                <a
                  data-testid={`branch-whatsapp-${b.slug}`}
                  href={`https://wa.me/${b.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/25 text-white px-6 py-3.5 font-bold uppercase text-xs tracking-[0.15em] hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  WhatsApp
                </a>
                <a
                  data-testid={`branch-map-${b.slug}`}
                  href={b.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/25 text-white px-6 py-3.5 font-bold uppercase text-xs tracking-[0.15em] hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  Open In Maps
                </a>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pb-24 md:pb-32">
        <Reveal>
          <h2 className="font-display uppercase text-2xl md:text-3xl mb-8">All Houses</h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {branches.map((br, i) => (
            <Reveal key={br.slug} delay={i * 0.1}>
              <Link
                data-testid={`branches-all-card-${br.slug}`}
                to={`/branches/${br.slug}`}
                className="group block overflow-hidden clip-frame border border-white/10 bg-panel h-full"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={br.image}
                    alt={`Oxygen Gym ${br.name}`}
                    className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                </div>
                <div className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Branch 0{i + 1}</p>
                  <h3 className="font-display uppercase text-xl">{br.name}</h3>
                  <p className="mt-2 text-xs text-white/50">{br.address}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
